import { useId, type ComponentPropsWithoutRef, type Ref } from 'react';

import { ChevronDownIcon, PlusIcon } from '@/components/icons';
import { cn } from '@/lib/utils/cn';

import { FIELD_INPUT, FIELD_LABEL, FIELD_STACK, fieldShell } from './field-shell';

export interface SelectOption {
  value: string;
  label: string;
  group?: string;
}

interface SelectProps extends Omit<ComponentPropsWithoutRef<'select'>, 'id' | 'children'> {
  label: string;
  options: readonly SelectOption[];
  /** Entrée vide en tête de liste. */
  placeholder?: string;
  /** Affiche une indication initiale sans en faire un choix sélectionnable. */
  placeholderDisabled?: boolean;
  error?: string | undefined;
  hint?: string;
  /** Affiche une action d'ajout intégrée au bord droit du champ. */
  onAdd?: (() => void) | undefined;
  addLabel?: string;
  addDisabled?: boolean;
  ref?: Ref<HTMLSelectElement>;
}

/**
 * Liste déroulante native, habillée comme les champs texte.
 *
 * Un `<select>` natif et non une liste maison : on garde gratuitement la
 * navigation au clavier, la recherche à la frappe, le rendu adapté au mobile
 * — un sélecteur plein écran sur téléphone — et l'intégration au formulaire.
 * Les remplaçants maison perdent presque toujours l'un des quatre.
 *
 * Le chevron est dessiné par-dessus, avec `pointer-events-none` : la flèche
 * native varie d'un navigateur à l'autre et ne suit pas la charte.
 */
export const Select = ({
  label,
  options,
  placeholder,
  placeholderDisabled = false,
  error,
  hint,
  className,
  ref,
  disabled,
  onAdd,
  addLabel = 'Ajouter une option',
  addDisabled = false,
  ...rest
}: SelectProps) => {
  const id = useId();
  const messageId = `${id}-message`;
  const hasError = Boolean(error);
  const grouped = new Map<string, SelectOption[]>();
  for (const option of options) {
    const key = option.group ?? '';
    grouped.set(key, [...(grouped.get(key) ?? []), option]);
  }

  return (
    <div className={FIELD_STACK}>
      <label htmlFor={id} className={FIELD_LABEL}>
        {label}
        {rest.required && !label.includes('*') ? <span aria-hidden="true"> *</span> : null}
      </label>

      <div className={fieldShell({ hasError, disabled: disabled ?? false })}>
        <select
          id={id}
          ref={ref}
          disabled={disabled}
          aria-invalid={hasError}
          aria-required={rest.required || label.includes('*') || undefined}
          aria-describedby={(error ?? hint) ? messageId : undefined}
          className={cn(
            FIELD_INPUT,
            // `appearance-none` retire la flèche native, remplacée ci-dessous.
            // `pr-10` réserve sa place pour que le libellé ne passe pas dessous.
            'h-[clamp(2.75rem,5.4vh,3.125rem)] cursor-pointer appearance-none rounded-lg pr-10 pl-4',
            onAdd && 'pr-24',
            className,
          )}
          {...rest}
        >
          {placeholder === undefined ? null : (
            <option value="" disabled={placeholderDisabled} hidden={placeholderDisabled}>
              {placeholder}
            </option>
          )}
          {[...grouped].map(([group, items]) =>
            group ? (
              <optgroup key={group} label={group}>
                {items.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ) : (
              items.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))
            ),
          )}
        </select>

        <ChevronDownIcon
          aria-hidden
          className={cn(
            'text-navy-500 pointer-events-none absolute text-base',
            onAdd ? 'right-16' : 'right-3.5',
          )}
        />
        {onAdd && (
          <button
            type="button"
            aria-label={addLabel}
            title={addLabel}
            disabled={disabled || addDisabled}
            onClick={onAdd}
            className="absolute inset-y-1 right-1 flex w-11 items-center justify-center rounded-r-md border-l border-navy-100 text-accent-600 transition-colors duration-200 hover:bg-accent-50 hover:text-accent-800 focus-visible:bg-accent-50 focus-visible:outline-2 focus-visible:outline-accent-400 disabled:cursor-not-allowed disabled:opacity-40 motion-reduce:transition-none"
          >
            <PlusIcon aria-hidden className="text-lg" />
          </button>
        )}
      </div>

      {error ? (
        <p id={messageId} role="alert" className="text-xs leading-5 text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={messageId} className="text-ink-500 text-xs leading-5">
          {hint}
        </p>
      ) : null}
    </div>
  );
};
